import { ApplicationUserDto, UserApiClient } from '../../api/api.generated';
import { Config } from '../../config';
import { AuthState } from '../auth-client/AuthState';
import { createAuthClientFetch } from '../auth-client/createAuthClientFetch';
import { authClient } from '../auth-client/instance';
import { AuthenticationChangeListener } from './AuthenticationChangeListener';

/** How long a failed cookie sync waits before retrying. */
const COOKIE_SYNC_RETRY_DELAY_MS = 10 * 1000;

/**
 * Handles user authentication and user profile data.
 *
 * Signing in happens on the Next.js site's onboarding, which creates an Auth
 * API cookie session on the shared country domain; this app discovers it
 * through the auth client and mirrors it into the user state. Signing out
 * destroys that session.
 */
export class AuthService {
    /**
     * The API client for the User api. Goes through the auth client fetch, so `/user/me` is authorized as the cookie
     * session's user and gets the same 401 invalidate-and-retry as every other api call.
     */
    private readonly userApiClient = new UserApiClient(Config.baseUrlApi, createAuthClientFetch());

    /** The current application user, or null while no user is signed in. */
    private applicationUser: ApplicationUserDto | null = null;

    /** The auth state the user state was last mirrored from, so unrelated auth state emissions are no-ops. */
    private syncedCookieAuthState: AuthState | null = null;

    /** Guards overlapping cookie syncs; only the latest one may apply its answer. */
    private cookieSyncRevision = 0;

    /** The retry timer of a failed cookie sync. */
    private cookieSyncRetryTimeoutId: number | null = null;

    /** Stops the auth client monitor started by this service. */
    private stopAuthClient: (() => void) | null = null;

    /** Unsubscribes the cookie auth state listener. */
    private unsubscribeCookieAuthState: (() => void) | null = null;

    /** Event listeners for when the authenticated user changes. */
    public readonly changeListeners: Set<AuthenticationChangeListener> = new Set();

    /**
     * Starts the auth client and resolves the initial user, then subscribes to the session so later changes are
     * mirrored. Paired with `destroy()`.
     */
    public async initialize(): Promise<ApplicationUserDto | null> {
        this.stopAuthClient = authClient.start();

        // The user state is resolved before reporting initialized, mirroring the Next.js site where "initialized"
        // means the auth system has answered.
        await this.syncCookieAuthentication();

        // Follows the Auth API session, so a sign-in, sign-out or user switch discovered by the auth client's
        // monitor — for instance from another tab — is mirrored into the user state.
        this.unsubscribeCookieAuthState = authClient.subscribeAuthState(() => {
            void this.syncCookieAuthentication();
        });

        // The boot sync above ran before the subscription existed, so a state change that landed while it was in
        // flight had no observer; reconcile once now that it does. When nothing changed this dedupes into a no-op.
        void this.syncCookieAuthentication();

        return this.applicationUser;
    }

    /**
     * Redirect to the onboarding with the path to return to after a successful login.
     *
     * The onboarding resolves `returnTo` as `<system>/<path>`, where the path is what this app's router sees — with the
     * locale, without the app prefix — so the onboarding builds the full url from its own configuration per system.
     *
     * @param onboardingUrl The url to the onboarding
     * @param continuePath The router path to return to after a successful login
     */
    public openLogin(onboardingUrl: string, continuePath: string): void {
        const targetUrl = new URL(onboardingUrl);
        targetUrl.searchParams.set('returnTo', `party${continuePath}`);

        document.location.assign(targetUrl);
    }

    /**
     * Signs the user out by destroying the Auth API cookie session.
     *
     * The destroy is best effort: nothing in the app surfaces a failed sign-out, so a failure is logged and swallowed
     * rather than thrown at a caller that has nowhere to put it.
     */
    public async logout(): Promise<void> {
        this.log('info', 'Destroying the Auth API session');

        // Destroying an already destroyed session is a no-op, so the session state is not consulted first — the local
        // view of it may be stale.
        //
        // The rejection handler is attached right away, so a rejection can never go unhandled: the auth client
        // resolves request failures into the result, but starting a sign-out while one is already running — a double
        // click — rejects instead.
        const error = await authClient.signOut().then(
            (result) => result.error,
            (caught: unknown) => caught,
        );

        if (error !== null) {
            // The user state is left alone, so it keeps describing the session that is still there. A later request
            // answers 401, the auth state converges to anonymous, and the cookie sync clears the user from there.
            this.log('warn', 'Failed to destroy the Auth API session', error);
            return;
        }

        this.updateAuthentication(null);
    }

    /**
     * Tears the service down: clears the change listeners and any pending cookie sync retry, unsubscribes from the
     * auth state, and releases this service's start of the auth client monitor.
     */
    public destroy(): void {
        this.changeListeners.clear();

        this.clearCookieSyncRetry();
        this.unsubscribeCookieAuthState?.();
        this.unsubscribeCookieAuthState = null;
        this.stopAuthClient?.();
        this.stopAuthClient = null;
    }

    /**
     * Updates the authenticated user and calls all the change listeners.
     */
    private updateAuthentication(applicationUser: ApplicationUserDto | null) {
        this.log('info', 'Updating the authenticated user and calling change listeners');
        this.applicationUser = applicationUser;

        for (const listener of this.changeListeners) {
            listener(this.applicationUser);
        }
    }

    /**
     * Mirrors the Auth API cookie session into the user state.
     *
     * The application user is fetched with the session's access token, so the user state always describes the
     * identity the api requests are made as.
     */
    private async syncCookieAuthentication(): Promise<void> {
        // The snapshot is taken before the requests: marking the state synced with a snapshot read afterwards could
        // stamp a state this sync never described, when the state flips while the requests are in flight.
        let cookieAuthState = authClient.getAuthState();

        if (cookieAuthState === this.syncedCookieAuthState) {
            // An older sync may still be in flight — the session flipped back to the already-synced state (the state
            // instances are shared) while it ran. Bumping here discards that flight: applied, it would mirror a user
            // the session no longer holds, and since this dedupe swallows the state's own emission, nothing would
            // ever correct it.
            this.cookieSyncRevision++;
            return;
        }

        const revision = ++this.cookieSyncRevision;
        this.clearCookieSyncRetry();

        try {
            // Waits for the auth client's first check; null always means a known anonymous session.
            const accessToken = await authClient.getAccessToken();

            if (cookieAuthState.status === 'initializing') {
                // The boot sync starts before the first check has answered; the settled state is the one this sync
                // describes and must mark as synced — stamping "initializing" would make every later comparison miss.
                cookieAuthState = authClient.getAuthState();
            }

            let applicationUser: ApplicationUserDto | null = null;

            if (accessToken !== null) {
                this.log('info', 'Fetching application user for the Auth API session');
                applicationUser = await this.userApiClient.apiUserMeGet();
            }

            // A state change while the requests were in flight starts a new sync; this one no longer describes the truth.
            if (revision !== this.cookieSyncRevision) {
                return;
            }

            this.syncedCookieAuthState = cookieAuthState;
            this.updateAuthentication(applicationUser);
        } catch (error: unknown) {
            this.log('warn', 'Failed to resolve the application user for the Auth API session', error);

            if (revision !== this.cookieSyncRevision) {
                return;
            }

            // The auth state only emits on changes, so nothing would rerun a failed sync while the state stays put —
            // without the retry, api requests would keep acting as a user the auth context never reports until the
            // next auth state change. An established user is kept over a transient failure.
            this.scheduleCookieSyncRetry();
        }
    }

    /**
     * Schedules a retry of a failed cookie sync. A newer sync clears it; a retry that finds the state already synced
     * is a no-op.
     */
    private scheduleCookieSyncRetry(): void {
        this.clearCookieSyncRetry();

        this.cookieSyncRetryTimeoutId = window.setTimeout((): void => {
            this.cookieSyncRetryTimeoutId = null;
            void this.syncCookieAuthentication();
        }, COOKIE_SYNC_RETRY_DELAY_MS);
    }

    private clearCookieSyncRetry(): void {
        if (this.cookieSyncRetryTimeoutId !== null) {
            window.clearTimeout(this.cookieSyncRetryTimeoutId);
            this.cookieSyncRetryTimeoutId = null;
        }
    }

    /**
     * Log to the console.
     */
    private log(severity: 'info' | 'warn', ...params: unknown[]): void {
        if (process.env.NODE_ENV !== 'development') {
            return;
        }

        if (severity === 'info') {
            console.info(`[${AuthService.name}]:`, ...params);
        } else {
            console.warn(`[${AuthService.name}]:`, ...params);
        }
    }
}
